import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { config } from '@/config'
import { STORAGE_KEYS } from '@/shared/constants'

/**
 * Create axios instance with base configuration
 */
const createHttpClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: config.apiUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: false,
  })

  return client
}

export const httpClient = createHttpClient()

export const clearAuthStorage = (clearCredentials = false) => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRES_AT)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRES_AT)
  localStorage.removeItem(STORAGE_KEYS.USER)
  localStorage.removeItem(STORAGE_KEYS.AUD_ROLE)
  localStorage.removeItem(STORAGE_KEYS.AUD_PERSONA)
  localStorage.removeItem(STORAGE_KEYS.AUD_SIDEBAR_COLLAPSED)

  if (clearCredentials) {
    localStorage.removeItem(STORAGE_KEYS.LOGIN_CREDENTIALS)
  }
}

const storeTokens = (
  accessToken: string,
  accessTokenExpiresAt: number,
  refreshToken?: string,
  refreshTokenExpiresAt?: number
) => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken)
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRES_AT, String(accessTokenExpiresAt))

  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
  }

  if (refreshTokenExpiresAt) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRES_AT, String(refreshTokenExpiresAt))
  }
}

const getExpiration = (key: string): number | null => {
  const value = localStorage.getItem(key)
  if (!value) return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

export const isAuthTokenValid = (): boolean => {
  const expiresAt = getExpiration(STORAGE_KEYS.AUTH_TOKEN_EXPIRES_AT)
  if (!expiresAt) return false
  return expiresAt > Date.now()
}

export const isRefreshTokenValid = (): boolean => {
  const expiresAt = getExpiration(STORAGE_KEYS.REFRESH_TOKEN_EXPIRES_AT)
  if (!expiresAt) return false
  return expiresAt > Date.now()
}

export const setAuthTokens = (
  accessToken: string,
  expiresIn: number,
  refreshToken?: string,
  refreshExpiresIn?: number
) => {
  const accessTokenExpiresAt = Date.now() + expiresIn * 1000
  const refreshTokenExpiresAt = refreshExpiresIn ? Date.now() + refreshExpiresIn * 1000 : undefined
  storeTokens(accessToken, accessTokenExpiresAt, refreshToken, refreshTokenExpiresAt)
}

export const isTokenValid = (token?: string): boolean => {
  if (!token) return false

  const parts = token.split('.')
  if (parts.length !== 3) {
    return false
  }

  try {
    const payloadPart = parts[1]
    if (!payloadPart) return false
    const payloadJson = atob(payloadPart)
    const payload = JSON.parse(payloadJson)

    if (!payload.exp) {
      return false
    }

    const expiry = Number(payload.exp) * 1000
    return !Number.isNaN(expiry) && expiry > Date.now()
  } catch {
    return false
  }
}

/**
 * Request interceptor
 */
httpClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    if (token) {
      const headers = (config.headers || {}) as any
      headers.Authorization = `Bearer ${token}`
      config.headers = headers
    }
    return config
  },
  (error) => Promise.reject(error)
)

let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

/**
 * Response interceptor
 */
httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
    const isLoginRequest = originalRequest?.url?.includes('/auth/login')
    const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh')

    // Don't redirect on login or refresh 401 - let the flows handle it
    if ((isLoginRequest || isRefreshRequest) && error.response?.status === 401) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              const headers = (originalRequest.headers || {}) as any
              headers.Authorization = `Bearer ${token}`
              originalRequest.headers = headers
              resolve(httpClient(originalRequest))
            },
            reject: (err: any) => {
              reject(err)
            },
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      // 1. Try Refresh Token first
      const rToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
      if (rToken && isRefreshTokenValid()) {
        try {
          const refreshResult = await axios.post(`${config.apiUrl}/auth/refresh`, {
            refresh_token: rToken,
          })
          const responseData = refreshResult.data
          const tokens = responseData?.data?.tokens ?? responseData?.data ?? responseData?.tokens ?? responseData
          const accessToken = tokens?.access_token ?? tokens?.accessToken

          if (accessToken) {
            const refreshToken = tokens?.refresh_token ?? tokens?.refreshToken ?? rToken
            const expiresIn = tokens?.expires_in ?? tokens?.expiresIn ?? 3600
            const refreshExpiresIn = tokens?.refresh_expires_in ?? tokens?.refreshExpiresIn

            setAuthTokens(accessToken, expiresIn, refreshToken, refreshExpiresIn)

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }

            processQueue(null, accessToken)
            isRefreshing = false
            return httpClient(originalRequest)
          }
        } catch (err) {
          console.error("Token refresh failed in interceptor", err)
        }
      }

      // 2. Fallback to Credentials auto-login
      const credsRaw = localStorage.getItem(STORAGE_KEYS.LOGIN_CREDENTIALS)
      if (credsRaw) {
        try {
          const credentials = JSON.parse(credsRaw)
          const loginResult = await axios.post(`${config.apiUrl}/auth/login`, credentials)
          if (loginResult.data?.data?.tokens) {
            const tokens = loginResult.data.data.tokens
            const accessToken = tokens.access_token
            const refreshToken = tokens.refresh_token
            const expiresIn = tokens.expires_in
            const refreshExpiresIn = tokens.refresh_expires_in

            setAuthTokens(accessToken, expiresIn, refreshToken, refreshExpiresIn)

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }

            processQueue(null, accessToken)
            isRefreshing = false
            return httpClient(originalRequest)
          }
        } catch (err) {
          console.error("Background auto-login failed during 401 handling", err)
        }
      }

      // If both options failed, clear auth and redirect to login
      processQueue(error, null)
      isRefreshing = false

      const isActualAuthError = error.response?.status === 401 || error.response?.status === 400
      clearAuthStorage(isActualAuthError)
      const loginRedirectPath = window.location.pathname.startsWith('/akashic-unstructured-data')
        ? '/akashic-unstructured-data/login'
        : '/'
      if (window.location.pathname !== loginRedirectPath) {
        window.location.href = loginRedirectPath
      }
      return Promise.reject(error)
    }

    // Handle when retry also returns 401
    if (error.response?.status === 401) {
      clearAuthStorage(true)
      const loginRedirectPath = window.location.pathname.startsWith('/akashic-unstructured-data')
        ? '/akashic-unstructured-data/login'
        : '/'
      if (window.location.pathname !== loginRedirectPath) {
        window.location.href = loginRedirectPath
      }
    }

    return Promise.reject(error)
  }
)

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

/**
 * API error response
 */
export interface ApiError {
  message: string
  errors?: Record<string, string[]>
}

